import React, { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { BaseRouteParams } from 'hocs'
import { useGetOrderDetailsByUuidQuery } from 'orders/reduxQueries'
import { IOrder, IOrderItem } from 'orders/schema'
import Money from 'uiComponents/money'
import {
    ExtensibleReactTableComponentProps,
    ReactTable,
    ReactTableRows,
    TableColumn,
} from 'uiComponents/table/reactTable'
import ItemOptions from './itemOptions'
import OrderItemStatus from './orderItemStatus'
import RedeemedOn from './redeemedOn'
import { Checkbox } from 'uiComponents/input'
import {
    getIsTicketRowCheckboxDisabled,
    getSelectedOrderById,
    getSelectedOrderItemsByTableIdAndRowId,
    getSelectedOrderItemsListByTableId,
    shouldToggleOrder,
    toggleOrder,
    toggleOrderItem,
} from 'orders/orders/redux'
import ValidTo from '../../commons/validTo'
import LongTextWithTooltip from 'uiComponents/typography/longTextWithTooltip'
import { useAppSelector } from 'store/hooks'
import { useHasFeatureFunc } from 'utils/useHasFeature'
import { convertTimeFormat, DateFormats, formatISOString } from 'utils/dates'
import { ReplaceMessagesFunc } from 'messagesContext'
import { LucideIcon } from 'utils/lucideIcon'
import { setHash } from 'utils/hash'
import './index.scss'
import { useTranslation } from 'translations'

const emptyArray = [] as any[]

interface Props {
    replaceMessages: ReplaceMessagesFunc
    hideMessage: (id: string) => void
}

const OrderDetails: React.FC<Props & ExtensibleReactTableComponentProps<IOrder>> = ({
    row,
    replaceMessages,
    hideMessage,
}) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const { accountSlug } = useParams<BaseRouteParams>()
    const { data, isFetching } = useGetOrderDetailsByUuidQuery({
        uuid: row.original.id,
        include: ['items', 'items.options', 'items.barcode', 'items.refund_info', 'items.membership', 'comment'],
    })
    const status = row.original.status
    const tableId = row.original.id
    const rowsSelected = useAppSelector(getSelectedOrderItemsListByTableId(tableId))
    const dispatch = useDispatch()
    const selectedOrders = useAppSelector(getSelectedOrderById(row.original.id))
    const hasFeature = useHasFeatureFunc()

    const handleViewMembership = (membershipUuid: string) => {
        setHash('membership_details', { membershipUuid })
    }

    const columns = useMemo(() => {
        const result: TableColumn<IOrderItem>[] = [
            {
                accessor: 'selectable',
                width: '.5rem',
                disableSortBy: true,
                disableHideColumn: true,
                Header: ({}) => {
                    return <div />
                },
                Cell: ({ row }) => {
                    const isSelected = useAppSelector(getSelectedOrderItemsByTableIdAndRowId(tableId, row.original.id))
                    const disabled = useAppSelector(getIsTicketRowCheckboxDisabled(tableId, row.original.id))
                    const orderShouldToggle = useAppSelector(shouldToggleOrder(tableId, row.original.id))
                    const isOrderSelected = useAppSelector(getSelectedOrderById(tableId))

                    return (
                        <Checkbox
                            id="select-ticket"
                            data-testid="orders-select-ticket-checkbox"
                            checked={isSelected || (!row.original.refundInfo && isOrderSelected)}
                            onChange={(e) => {
                                dispatch(
                                    toggleOrderItem({
                                        rowId: row.original.id,
                                        tableId: tableId,
                                    }),
                                )
                                if (orderShouldToggle) {
                                    dispatch(toggleOrder({ orderUuid: tableId, selected: e.target.checked }))
                                }
                            }}
                            disabled={disabled}
                        />
                    )
                },
            },
            {
                Header: t('orders.orders.ordersTable.details.product'),
                style: { fontWeight: 400 },
                accessor: 'product',
                width: '11.313rem',
                Cell: ({ value, row }) => (
                    <>
                        <LongTextWithTooltip text={value}>{value}</LongTextWithTooltip>
                        {row.original.membership && (
                            <div
                                className="view-membership"
                                onClick={() => {
                                    handleViewMembership(row.original.membership!)
                                }}
                            >
                                <LucideIcon iconName="PanelRightOpen" />
                            </div>
                        )}
                    </>
                ),
            },
            {
                Header: t('orders.orders.ordersTable.details.productId'),
                accessor: 'productNumericId',
                width: '123px',
                Cell: ({ value, row }) => (
                    <Link
                        title={value}
                        to={`/account/${accountSlug}/products/crud/${row.original.productId}`}
                        target="__blank"
                    >
                        {value}
                    </Link>
                ),
            },
            {
                Header: t('orders.orders.ordersTable.details.barcode'),
                style: { fontWeight: 400 },
                accessor: 'barcode.barcode',
                width: '181px',
                Cell: ({ value }) => <LongTextWithTooltip text={value}>{value}</LongTextWithTooltip>,
            },
            {
                Header: t('orders.orders.ordersTable.details.redeemedOn'),
                style: { fontWeight: 400 },
                accessor: 'barcode.redeemed',
                width: '9.875rem',
                Cell: ({ value }) => <RedeemedOn value={value} />,
            },
            {
                Header: t('orders.orders.ordersTable.details.amountExclTax'),
                features: ['OrdersListPricesExcludingTax'],
                style: { fontWeight: 400 },
                accessor: 'priceExclTax',
                width: '10rem',
                Cell: ({ value }) => <Money amount={value} accountSlug={accountSlug} />,
            },
            {
                Header: hasFeature('OrdersListPricesExcludingTax') ? t('orders.orders.ordersTable.details.amountInclTax') : t('orders.orders.ordersTable.details.amount'),
                style: { fontWeight: 400 },
                accessor: 'priceInclTax',
                width: '10rem',
                Cell: ({ value }) => <Money amount={value} accountSlug={accountSlug} />,
            },

            {
                Header: t('orders.orders.ordersTable.details.visitDate'),
                style: { fontWeight: 400 },
                accessor: 'visitDate',
                width: '7.688rem',
                Cell: ({ value }) => (value ? formatISOString(value, DateFormats.LONG_DATE) : '-'),
            },
            {
                Header: t('orders.orders.ordersTable.details.visitTime'),
                style: { fontWeight: 400 },
                accessor: 'visitTime',
                width: '7.688rem',
                Cell: ({ value }) => (value ? convertTimeFormat(value, 'HH:mm:ss', DateFormats.SHORT_TIME) : '-'),
            },
            {
                Header: t('orders.orders.ordersTable.details.validFrom'),
                style: { fontWeight: 400 },
                accessor: 'validFrom',
                width: '7.688rem',
                Cell: ({ value }) => (value ? formatISOString(value, DateFormats.LONG_DATE) : '-'),
            },
            {
                Header: t('orders.orders.ordersTable.details.validTo'),
                style: { fontWeight: 400 },
                accessor: 'validTo',
                width: '7.688rem',
                Cell: ({ row: { original } }) => <ValidTo status={status} orderItem={original} order={data} />,
            },
            {
                Header: t('orders.orders.ordersTable.details.status'),
                style: { fontWeight: 400 },
                accessor: 'status',
                width: '7.688rem',
                Cell: ({ value }) => <OrderItemStatus value={value} />,
            },
        ]

        return result.filter((column: TableColumn<IOrderItem>) => {
            if (column.features) {
                return column.features.every((feature) => hasFeature(feature))
            }
            return true
        })
    }, [accountSlug, row.original.id, data, status, tableId])

    const renderItemOptions = useCallback((row: ReactTableRows<IOrderItem>) => {
        return (
            <ItemOptions
                data={row.original?.options}
                shouldShowPricesWithoutTax={hasFeature('OrdersListPricesExcludingTax')}
            />
        )
    }, [])

    return (
        <div style={{ display: 'flex' }} className="orders-details">
            <ReactTable
                loading={isFetching}
                noResultsRow
                rowSelect
                storeStateInQuery={false}
                rowContainerProps={(row: ReactTableRows<IOrderItem>) => ({
                    style: {
                        borderRadius: 4,
                        border: `1px solid ${
                            !!row.original.refundInfo
                                ? theme.colors.sunrise
                                : rowsSelected.includes(row.original.id)
                                ? theme.colors.boyBlue
                                : theme.colors.aluminiumShades[5]
                        }`,
                        flexWrap: 'wrap',
                        backgroundColor: rowsSelected.includes(row.original.id)
                            ? theme.colors.boyBlueShades[5]
                            : theme.colors.white,
                        marginBottom: '0.5rem',
                    },
                })}
                rowProps={() => ({
                    style: {
                        height: 32,
                        fontSize: '0.75rem',
                    },
                    rowFooter: renderItemOptions,
                })}
                tableProps={{ style: { marginRight: '5rem', fontWeight: 400 } }}
                elevation={false}
                columns={columns}
                data={data?.items || emptyArray}
                backgroundColor="transparent"
                headerProps={{
                    style: {
                        backgroundColor: selectedOrders ? theme.colors.aluminium : 'inherit',
                        color: selectedOrders ? theme.colors.white : theme.colors.aluminium,
                    },
                }}
                innerTable={true}
            />
        </div>
    )
}

export default OrderDetails
